import { useState, useEffect, useCallback } from 'react';
import { s, authHeaders, buildODataUrl, Toast, StandardDataGrid, statusPill, gridActionButton, newRecordButtonStyle, getODataRows, resolveODataTotalCount } from './listPageStyles';

const API_BASE = '/api/warehouses';

const EMPTY_FORM = {
  textId: '',
  name: '',
  adress: '',
  description: '',
  erpId: '',
  category: '',
  status: '',
  vendorId: '',
};

function unwrapLookupItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.Data)) return payload.Data;
  if (Array.isArray(payload?.value)) return payload.value;
  return [];
}

function toRequiredInt(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function compareText(a, b, direction) {
  const left = (a || '').toString().toLocaleLowerCase('tr-TR');
  const right = (b || '').toString().toLocaleLowerCase('tr-TR');
  const result = left.localeCompare(right, 'tr');
  return direction === 'asc' ? result : -result;
}

export function WarehousesPageLegacy() {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [vendors, setVendors] = useState([]);

  const [sortField, setSortField] = useState('TextId');
  const [sortDir, setSortDir] = useState('asc');
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const getVendorLabel = useCallback((vendorId) => {
    const vendor = vendors.find(item => item.id === vendorId);
    if (!vendor) return '-';
    return vendor.name ? `${vendor.textId || ''} - ${vendor.name}`.trim().replace(/^- /, '') : vendor.textId || '-';
  }, [vendors]);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    fetch('/api/vendors/Select', { headers: authHeaders() })
      .then(res => res.ok ? res.json() : [])
      .then(data => setVendors(unwrapLookupItems(data)))
      .catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let filterParams = '';
      if (filterText.trim()) {
        const q = filterText.trim().replace(/'/g, "''").toLowerCase();
        filterParams = `(contains(tolower(TextId),'${q}') or contains(tolower(Name),'${q}') or contains(tolower(ErpId),'${q}') or contains(tolower(Description),'${q}'))`;
      }

      if (sortField === 'VendorLabel') {
        const url = buildODataUrl(API_BASE, { pageSize: 100000, currentPage: 1, sortField: 'TextId', sortDir: 'asc', filterParams });
        const res = await fetch(url, { headers: authHeaders() });
        if (!res.ok) throw new Error('Veri alinamadi');
        const data = await res.json();
        const allRows = getODataRows(data);
        const skip = (currentPage - 1) * pageSize;
        const sortedRows = [...allRows].sort((a, b) => compareText(getVendorLabel(a.vendorId), getVendorLabel(b.vendorId), sortDir));
        setRows(sortedRows.slice(skip, skip + pageSize));
        setTotalCount(sortedRows.length);
        return;
      }

      const url = buildODataUrl(API_BASE, { pageSize, currentPage, sortField, sortDir, filterParams });
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('Veri alinamadi');
      const data = await res.json();
      const pageRows = getODataRows(data);
      setRows(pageRows);
      setTotalCount(await resolveODataTotalCount({ data, rows: pageRows, currentPage, pageSize, base: API_BASE, filterParams, headers: authHeaders() }));
    } catch (error) {
      showToast('error', error.message || 'Veri alinirken hata olustu');
    } finally {
      setLoading(false);
    }
  }, [filterText, sortField, sortDir, pageSize, currentPage, getVendorLabel]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFilterSearch = () => {
    setFilterText(filterInput);
    setCurrentPage(1);
  };

  const clearFilter = () => {
    setFilterText('');
    setFilterInput('');
    setCurrentPage(1);
  };

  const rowToForm = (row) => ({
    textId: row.textId || '',
    name: row.name || '',
    adress: row.adress || '',
    description: row.description || '',
    erpId: row.erpId || '',
    category: row.category ?? '',
    status: row.status ?? '',
    vendorId: row.vendorId || '',
  });

  const openNew = () => {
    setForm(EMPTY_FORM);
    setSelected(null);
    setModal('new');
  };

  const openEdit = (row = selected) => {
    if (!row) return;
    setSelected(row);
    setForm(rowToForm(row));
    setModal('edit');
  };

  const openView = (row = selected) => {
    if (!row) return;
    setSelected(row);
    setForm(rowToForm(row));
    setModal('view');
  };

  const closeModal = () => setModal(null);

  const handleSave = async () => {
    if (!form.textId.trim()) {
      showToast('error', 'Depo kodu bos olamaz');
      return;
    }
    if (!form.name.trim()) {
      showToast('error', 'Depo adi bos olamaz');
      return;
    }
    if (!form.vendorId) {
      showToast('error', 'Tedarikci secimi zorunludur');
      return;
    }

    const payload = {
      textId: form.textId,
      name: form.name,
      adress: form.adress || null,
      description: form.description || null,
      erpId: form.erpId || null,
      category: toRequiredInt(form.category),
      status: toRequiredInt(form.status),
      vendorId: form.vendorId,
    };

    try {
      const res = await fetch(`${API_BASE}/${modal === 'new' ? 'Add' : 'Update'}`, {
        method: modal === 'new' ? 'POST' : 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(modal === 'new' ? payload : { id: selected.id, ...payload }),
      });
      if (!res.ok) throw new Error(modal === 'new' ? 'Kayit eklenemedi' : 'Kayit guncellenemedi');
      showToast('success', modal === 'new' ? 'Depo basariyla eklendi' : 'Depo basariyla guncellendi');
      closeModal();
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Islem sirasinda hata olustu');
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ id: selected.id }),
      });
      if (!res.ok) throw new Error('Kayit silinemedi');
      showToast('success', 'Depo basariyla silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme islemi sirasinda hata olustu');
      setDeleteConfirm(false);
    }
  };

  const isView = modal === 'view';
  const inputStyle = isView ? s.inputView : s.input;
  const selectStyle = isView ? s.selectView : s.select;
  const columns = [
    { field: 'textId', headerName: 'Depo Kodu', flex: 1, minWidth: 140 },
    { field: 'name', headerName: 'Depo Adi', flex: 1.2, minWidth: 180 },
    {
      field: 'vendorLabel',
      headerName: 'Tedarikci',
      flex: 1.3,
      minWidth: 200,
      renderCell: (params) => getVendorLabel(params.row.vendorId),
    },
    { field: 'category', headerName: 'Kategori', width: 120 },
    { field: 'status', headerName: 'Durum', width: 130, renderCell: (params) => statusPill(params.row.status) },
    {
      field: 'erpId',
      headerName: 'ERP Kodu',
      flex: 1,
      minWidth: 130,
      renderCell: (params) => params.row.erpId || '-',
    },
    {
      field: 'actions',
      headerName: 'Islemler',
      width: 250,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', height: '100%' }}>
          <button style={{ ...gridActionButton, background: 'var(--accent)' }} onClick={(event) => { event.stopPropagation(); openView(params.row); }}>Goruntule</button>
          <button style={{ ...gridActionButton, background: 'var(--warn)' }} onClick={(event) => { event.stopPropagation(); openEdit(params.row); }}>Degistir</button>
          <button style={{ ...gridActionButton, background: '#E53E3E', minWidth: 48 }} onClick={(event) => { event.stopPropagation(); setSelected(params.row); setDeleteConfirm(true); }}>Sil</button>
        </div>
      ),
    },
  ];

  return (
    <div style={s.container}>
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h2 style={s.pageTitle}>Depo Yonetimi</h2>
          <p style={s.pageSubtitle}>Depo kayitlarini filtreleyin, siralayin ve yonetin.</p>
        </div>
        <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Depo</button>
      </div>

      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          <input
            style={s.filterInput}
            type="text"
            placeholder="Depo kodu, adi, ERP veya aciklama ile filtrele..."
            value={filterInput}
            onChange={e => setFilterInput(e.target.value)}
            onKeyDown={event => { if (event.key === 'Enter') handleFilterSearch(); }}
          />
          {filterText && <button style={s.btnSecondary} onClick={clearFilter}>Temizle</button>}
          <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
        </div>
      </div>

      <StandardDataGrid
        rows={rows}
        columns={columns}
        totalCount={totalCount}
        loading={loading}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={page => setCurrentPage(page)}
        onPageSizeChange={size => { setPageSize(size); setCurrentPage(1); }}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={(field, direction) => { setCurrentPage(1); setSortField(field || 'TextId'); setSortDir(direction || 'asc'); }}
        selectedId={selected?.id}
        onRowClick={row => setSelected(row)}
        onRowDoubleClick={row => openView(row)}
      />

      {modal && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={{ ...s.modal, maxWidth: 720 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>
                {modal === 'new' ? 'Yeni Depo' : modal === 'edit' ? 'Depo Duzenle' : 'Depo Detay'}
              </h3>
              <button style={s.closeBtn} onClick={closeModal}>x</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Depo Kodu *</label>
                  <input style={inputStyle} value={form.textId} onChange={e => setForm(current => ({ ...current, textId: e.target.value }))} readOnly={isView} autoFocus />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Depo Adi *</label>
                  <input style={inputStyle} value={form.name} onChange={e => setForm(current => ({ ...current, name: e.target.value }))} readOnly={isView} />
                </div>
              </div>

              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Tedarikci *</label>
                  {isView ? (
                    <input style={s.inputView} readOnly value={getVendorLabel(form.vendorId)} />
                  ) : (
                    <select style={selectStyle} value={form.vendorId} onChange={e => setForm(current => ({ ...current, vendorId: e.target.value }))}>
                      <option value="">Seciniz</option>
                      {vendors.map(vendor => (
                        <option key={vendor.id} value={vendor.id}>
                          {vendor.textId}{vendor.name ? ` - ${vendor.name}` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>ERP Kodu</label>
                  <input style={inputStyle} value={form.erpId} onChange={e => setForm(current => ({ ...current, erpId: e.target.value }))} readOnly={isView} />
                </div>
              </div>

              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Kategori</label>
                  <input style={inputStyle} value={form.category} onChange={e => setForm(current => ({ ...current, category: e.target.value }))} readOnly={isView} />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Durum</label>
                  <input style={inputStyle} value={form.status} onChange={e => setForm(current => ({ ...current, status: e.target.value }))} readOnly={isView} />
                </div>
              </div>

              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Adres</label>
                  <input style={inputStyle} value={form.adress} onChange={e => setForm(current => ({ ...current, adress: e.target.value }))} readOnly={isView} />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Aciklama</label>
                  <input style={inputStyle} value={form.description} onChange={e => setForm(current => ({ ...current, description: e.target.value }))} readOnly={isView} />
                </div>
              </div>
            </div>
            <div style={s.modalFooter}>
              {!isView && <button style={s.btnPrimary} onClick={handleSave}>Kaydet</button>}
              <button style={s.btnSecondary} onClick={closeModal}>{isView ? 'Kapat' : 'Iptal'}</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayi</h3>
            </div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>
                <strong>"{selected?.name}"</strong> deposunu silmek istediginize emin misiniz?
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>Iptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}
